/** Search endpoint calls — full-text search for posts and accounts. */
import type { AccountWithStats, PaginatedResponse, FeedItem, MapPinsResponse } from '@olimhub/shared'
import type { Language, PostType } from '@olimhub/shared'
import { apiClient } from '../client'

export interface TagResult { tag: string; count: number }

export interface SearchAccountsParams {
  cursor?: string
  limit?: number
  languages?: Language[]
  // Only Search's People mode + its "Tudo" account strip pass this — other
  // callers (DM, group invite, Action attestation, @mention autocomplete)
  // must keep seeing group accounts, so it's opt-in, not a default.
  excludeGroups?: boolean
}

export interface SearchPostsParams {
  type?: PostType
  language?: Language
  cursor?: string
  status?: 'open' | 'resolved'
  // Type-conditional facets (Event/Bazaar/Help/Action) — only meaningful
  // (and only ever sent) when `type` is a single specific post type.
  category?: string
  group?: string
  startsAfter?: string
  startsBefore?: string
  urgent?: boolean
  // Location facet (Event/Action only — spec-search-localizacao-event-
  // action-groups.md). 'profile' needs no reference point (server resolves
  // it from the JWT); 'address'/'map_center' require referenceLat/Lng.
  locationMode?: 'profile' | 'address' | 'map_center'
  radiusKm?: number
  referenceLat?: number
  referenceLng?: number
}

// Same facet shape as SearchPostsParams, minus what the map layer never
// needs (language/status/urgent — Help isn't geolocated, and the map
// always excludes past items server-side regardless of these).
export interface SearchMapParams {
  category?: string
  group?: string
  startsAfter?: string
  startsBefore?: string
  locationMode?: 'profile' | 'address' | 'map_center'
  radiusKm?: number
  referenceLat?: number
  referenceLng?: number
}

export const searchApi = {
  searchPosts: (q: string, params?: SearchPostsParams) =>
    apiClient
      .get<PaginatedResponse<FeedItem>>('/search/posts', { params: { q, ...params } })
      .then((r) => r.data),

  getMapPins: (type: 'event' | 'action', q: string, params?: SearchMapParams) =>
    apiClient
      .get<MapPinsResponse>('/search/posts/map', { params: { type, q, ...params } })
      .then((r) => r.data),

  searchAccounts: (q: string, params?: SearchAccountsParams) =>
    apiClient
      .get<PaginatedResponse<AccountWithStats>>('/search/accounts', {
        params: { q, ...params, languages: params?.languages?.length ? params.languages.join(',') : undefined },
      })
      .then((r) => r.data),

  searchTags: (q: string) =>
    apiClient.get<TagResult[]>('/search/tags', { params: { q } }).then((r) => r.data),

  getTrendingTags: () =>
    apiClient.get<TagResult[]>('/search/tags/trending').then((r) => r.data),
}
